import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAvailableSlots,
  getOnboardingStatus,
  recordCalendlyBooking,
  submitOnboardingForm,
} from '@/lib/onboarding';
import type {
  AvailableSlot,
  CalendlyBookedResponse,
  CalendlyBookingInput,
  OnboardingFormInput,
  OnboardingStatus,
} from './schemas';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/auth';

/**
 * Onboarding status is workspace-scoped on the backend — it counts CONNECTED
 * accounts and RUNNING warmup states inside the JWT-bound workspace. The
 * cache key MUST include the workspace id so that switching workspaces
 * doesn't reuse the previous workspace's "all done" blob (which is what
 * causes the empty Email-Accounts hero to render with all three onboarding
 * steps green).
 *
 * `statuses()` returns the workspace-agnostic prefix used by mutation
 * invalidations; `status(workspaceId)` returns the full key for a specific
 * workspace's cache slot. TanStack Query's `invalidateQueries` matches by
 * prefix by default, so `invalidateQueries({ queryKey: statuses() })`
 * invalidates every workspace's cached status at once — correct for global
 * fan-out (e.g. completing the onboarding form) and harmless for
 * per-workspace mutations (the other workspaces will just refetch the same
 * answer they already had).
 */
export const onboardingKeys = {
  all: ['onboarding'] as const,
  statuses: () => [...onboardingKeys.all, 'status'] as const,
  status: (workspaceId: number | null) =>
    [...onboardingKeys.statuses(), workspaceId ?? 'unauth'] as const,
  slots: (startTime?: string) =>
    [...onboardingKeys.all, 'slots', startTime ?? 'default'] as const,
};

export function useOnboardingStatusQuery() {
  const workspaceId = useAuthStore((s) => s.workspace?.id ?? null);
  return useQuery<OnboardingStatus>({
    // Disable until a workspace is known — otherwise the first render under
    // an unauthenticated user fires a request that 401s and then refetches
    // once auth lands, with no cache reuse for the new key.
    enabled: workspaceId !== null,
    queryKey: onboardingKeys.status(workspaceId),
    queryFn: getOnboardingStatus,
    // 401 is also a non-retry — the global retry already handles it.
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 2;
    },
  });
}

export function useAvailableSlotsQuery(startTime?: string, enabled = true) {
  return useQuery<AvailableSlot[]>({
    queryKey: onboardingKeys.slots(startTime),
    queryFn: () => getAvailableSlots(startTime),
    enabled,
  });
}

export function useSubmitOnboardingFormMutation() {
  const qc = useQueryClient();
  const workspaceId = useAuthStore((s) => s.workspace?.id ?? null);
  return useMutation<OnboardingStatus, Error, OnboardingFormInput>({
    mutationFn: submitOnboardingForm,
    onSuccess: (next) => {
      // Seed the current workspace's slot directly so the next render of
      // OnboardingProvider sees the post-submit status without an extra
      // round-trip. Other workspaces' slots are invalidated via the prefix —
      // the form is per-user, not per-workspace, but `formCompleted` is
      // returned on every workspace's status DTO so they all need to refetch.
      qc.setQueryData(onboardingKeys.status(workspaceId), next);
      void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
    },
  });
}

export function useRecordCalendlyBookingMutation() {
  const qc = useQueryClient();
  return useMutation<CalendlyBookedResponse, Error, CalendlyBookingInput>({
    mutationFn: recordCalendlyBooking,
    // CalendlyStep renders its own inline AlertBanner for this failure — the
    // global MutationCache onError toast would double up the same message.
    meta: { silentError: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
    },
  });
}
